import { callFunction, callFunctionGet, callFunctionPatch } from "./client";

export const CPF_MASK = "***.***.***-**";

export type MembershipCategory = "colaborador" | "efetivo";
export type MemberStatus = "ativo" | "suspenso_disciplinar" | "desligado";
export type ElectedRole = "diretoria" | "conselho_deliberativo" | "conselho_fiscal";

export interface MemberSummary {
  id: string;
  full_name: string;
  preferred_name: string | null;
  membership_category: MembershipCategory;
  status: MemberStatus;
  effective_since: string | null;
  username: string | null;
  elected_roles?: ElectedRole[];
}

export interface DepartmentAssignment {
  id: string;
  department: string;
  activity: string | null;
  role: string | null;
  started_at: string;
  ended_at: string | null;
}

export interface CreateMemberInput {
  full_name: string;
  cpf: string;
  birth_date: string;
  email: string;
  phone: string;
  username: string;
}

export interface CreateMemberResponse {
  member_id: string;
  username: string;
  temporary_password: string;
  requires_password_change: true;
}

export interface MemberContactPatch {
  preferred_name?: string | null;
  phone?: string;
}

export interface MemberCorrectionPatch {
  full_name?: string;
  preferred_name?: string | null;
  email?: string;
  phone?: string;
  birth_date?: string;
}

export type DepartmentCode = "DAF" | "DAE" | "DEIJ" | "DAPS" | "DAFA" | "DE";

export interface CreateDepartmentAssignmentInput {
  department: DepartmentCode;
  activity: string;
  role: string;
  started_at: string;
}

export interface UpdateDepartmentAssignmentInput {
  activity: string;
  role: string;
  ended_at: string | null;
}

export type CredentialResetTarget =
  | { member_id: string; login_identity_id?: never }
  | { login_identity_id: string; member_id?: never };

export type ResetPasswordResponse =
  | ({ status: "completed"; temporary_password: string; requires_password_change: true } & CredentialResetTarget)
  | { status: "already_completed"; message: string }
  | { status: "completed_without_recoverable_password"; message: string };

export interface MemberProfile extends MemberSummary {
  email: string | null;
  phone: string | null;
  birth_date: string | null;
  cpf: string;
  department_assignments: DepartmentAssignment[];
}

export interface CategoryHistoryItem {
  previous_category: MembershipCategory;
  new_category: MembershipCategory;
  changed_at: string;
  justification: string;
  directoria_decision_reference: string;
  council_homologation_reference: string | null;
}

export interface MemberHistory extends MemberProfile {
  category_history: CategoryHistoryItem[];
}

export interface MembersPageResponse {
  items: MemberSummary[];
  total: number;
  page: number;
  page_size: number;
}

export interface MemberFiltersInput {
  search?: string;
  category?: MembershipCategory;
  page: number;
  pageSize: number;
}

export function getMembers(accessToken: string, filters: MemberFiltersInput): Promise<MembersPageResponse> {
  return callFunctionGet<MembersPageResponse>(
    "members-search",
    {
      search: filters.search,
      category: filters.category,
      page: filters.page,
      page_size: filters.pageSize,
    },
    { accessToken },
  );
}

export function getMemberDetail(accessToken: string, memberId: string): Promise<MemberProfile> {
  return callFunctionGet<MemberProfile>("member-detail", { member_id: memberId }, { accessToken });
}

export function getMemberHistory(accessToken: string, memberId: string): Promise<MemberHistory> {
  return callFunctionGet<MemberHistory>("member-history", { member_id: memberId }, { accessToken });
}

export function createMember(accessToken: string, input: CreateMemberInput): Promise<CreateMemberResponse> {
  return callFunction<CreateMemberInput, CreateMemberResponse>("members-create", input, { accessToken });
}

export function updateMemberContact(accessToken: string, memberId: string, patch: MemberContactPatch): Promise<{ member_id: string }> {
  return callFunctionPatch<MemberContactPatch, { member_id: string }>("member-self-update-contact", { member_id: memberId }, patch, { accessToken });
}

export function correctMember(accessToken: string, memberId: string, patch: MemberCorrectionPatch): Promise<{ member_id: string }> {
  return callFunctionPatch<MemberCorrectionPatch, { member_id: string }>("member-correct", { member_id: memberId }, patch, { accessToken });
}

export function createDepartmentAssignment(accessToken: string, memberId: string, input: CreateDepartmentAssignmentInput): Promise<{ assignment_id: string }> {
  return callFunction<CreateDepartmentAssignmentInput, { assignment_id: string }>("member-department-assignment", input, { accessToken }, { member_id: memberId });
}

export function updateDepartmentAssignment(accessToken: string, assignmentId: string, input: UpdateDepartmentAssignmentInput): Promise<{ assignment_id: string }> {
  return callFunctionPatch<UpdateDepartmentAssignmentInput, { assignment_id: string }>("member-department-assignment", { assignment_id: assignmentId }, input, { accessToken });
}

export function resetMemberPassword(accessToken: string, target: CredentialResetTarget, operationId: string): Promise<ResetPasswordResponse> {
  return callFunction<CredentialResetTarget & { operation_id: string }, ResetPasswordResponse>(
    "credentials-reset-password",
    { ...target, operation_id: operationId },
    { accessToken },
  );
}
